import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { CtaBannerProps, PageHeroProps } from '../types';
import { CONTACT, WHATSAPP_LINK } from '../data/constants';
import { Phone, Mail, MapPin, MessageCircle, Tag, Ribbon, Printer, Scan, Gem, Shirt, ShoppingCart, Factory, Package, HeartPulse, Wrench } from 'lucide-react';

export const Footer: FC = () => (
  <footer className="site-footer">
    <div className="container">
      <div className="footer-grid">
        <div className="footer-col-brand">
          <div className="footer-brand-name">DevBhoomi Labels</div>
          <p className="footer-brand-desc">India's trusted partner for barcode labels, printers, scanners and complete POS solutions. Quality products with dedicated after-sales support.</p>
          <div className="fcon"><Phone size={13} /><a href={`tel:${CONTACT.phone1}`}>{CONTACT.phone1}</a></div>
          <div className="fcon"><Phone size={13} /><a href={`tel:${CONTACT.phone2}`}>{CONTACT.phone2}</a></div>
          <div className="fcon"><Mail size={13} /><a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a></div>
          <div className="fcon"><MapPin size={13} /><a href={CONTACT.mapLink} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>{CONTACT.address}</a></div>
        </div>
        <div className="footer-col">
          <h4>Products</h4>
          <ul>
            <li><Link to="/barcode-labels"><Tag size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Barcode Labels</Link></li>
            <li><Link to="/ttr-ribbons"><Ribbon size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />TTR Ribbons</Link></li>
            <li><Link to="/printers"><Printer size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Barcode Printers</Link></li>
            <li><Link to="/scanners"><Scan size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Barcode Scanners</Link></li>
            <li><Link to="/jewellery-labels"><Gem size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Jewellery Labels</Link></li>
            <li><Link to="/taffeta-labels"><Shirt size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Taffeta Labels</Link></li>
            <li><Link to="/retail-pos"><ShoppingCart size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Retail POS</Link></li>
          </ul>
        </div>
        <div className="footer-col">
          <h4>Industries</h4>
          <ul>
            <li><Link to="/industries/manufacturing"><Factory size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Manufacturing</Link></li>
            <li><Link to="/industries/retail"><ShoppingCart size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Retail</Link></li>
            <li><Link to="/industries/healthcare"><HeartPulse size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Healthcare</Link></li>
            <li><Link to="/industries/logistics"><Package size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Logistics</Link></li>
            <li><Link to="/industries/pharmaceutical">Pharma</Link></li>
            <li><Link to="/industries/ecommerce">E-commerce</Link></li>
            <li><Link to="/industries">View All</Link></li>
          </ul>
        </div>
        <div className="footer-col">
          <h4>Company</h4>
          <ul>
            <li><Link to="/company">About Us</Link></li>
            <li><Link to="/services"><Wrench size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />AMC / Repair</Link></li>
            <li><Link to="/software">Software</Link></li>
            <li><Link to="/branches">Our Branches</Link></li>
            <li><Link to="/contact"><Phone size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Contact Us</Link></li>
            <li><a href={WHATSAPP_LINK} target="_blank" rel="noreferrer"><MessageCircle size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />WhatsApp Us</a></li>
          </ul>
        </div>
      </div>
      <hr className="footer-divider" />
      <div className="footer-bottom">
        <p>© 2024 DevBhoomi Labels · Dehradun, Uttarakhand · All rights reserved.</p>
        <p>Barcode Solutions · Label Printing · POS Systems · India</p>
      </div>
    </div>
  </footer>
);

export const PageHero: FC<PageHeroProps> = ({ breadcrumbs, title, subtitle }) => (
  <div className="page-hero">
    <div className="container">
      <nav className="breadcrumb" aria-label="breadcrumb">
        {breadcrumbs.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="sep">/</span>}
            {c.link ? <Link to={c.link}>{c.label}</Link> : <span>{c.label}</span>}
          </React.Fragment>
        ))}
      </nav>
      <h1>{title}</h1>
      <p>{subtitle}</p>
    </div>
  </div>
);

export const CtaBanner: FC<CtaBannerProps> = ({ image, title, subtitle, primaryBtnText, primaryBtnLink, secondaryBtnText, secondaryBtnLink, secondaryIsExternal }) => (
  <div className="cta-banner">
    <img className="cta-bg" src={image} alt="" onError={(e) => { (e.target as HTMLImageElement).style.background = '#0a2540'; }} />
    <div className="cta-overlay">
      <div>
        <h2>{title}</h2>
        <p>{subtitle}</p>
        <div className="cta-btns">
          <Link to={primaryBtnLink} className="btn btn-white">{primaryBtnText}</Link>
          {secondaryBtnText && secondaryBtnLink && (
            secondaryIsExternal
              ? <a href={secondaryBtnLink} className="btn btn-outline" target="_blank" rel="noreferrer">{secondaryBtnText}</a>
              : <Link to={secondaryBtnLink} className="btn btn-outline">{secondaryBtnText}</Link>
          )}
        </div>
      </div>
    </div>
  </div>
);
